import endpoints from "@config/endpoints"
import { loginProviders } from "@config/enums"
import { prepareGoogleContacts } from "@utils/contacts"
import { getItem } from "@utils/localStorage"
import api, { customCalInstance } from "api"
import { store } from "index"

import actionTypes from "./actionTypes"

const getContacts = () => ({
  type: actionTypes.GET_CONTACTS,
})

const fetchBookContacts = () => {
  api.client
    .get(endpoints.fetchContacts())
    .then(response => {
      store.dispatch(gotBookContacts(response.data.data))
    })
    .catch(error => {
      console.log("fetchUserTeeupsUsers error ")
      console.log(error)
    })
}

const gotBookContacts = contacts => ({
  type: actionTypes.GOT_BOOK_CONTACTS,
  payload: { contacts },
})

const addTagsToUsers = (userId, body, onTagUsersEnd) => ({
  type: actionTypes.ADD_TAGS_TO_USERS,
  payload: { userId, body, onTagUsersEnd },
})

const removeTagsFromUsers = (userId, body, onTagUsersEnd) => ({
  type: actionTypes.REMOVE_TAGS_FROM_USERS,
  payload: { userId, body, onTagUsersEnd },
})

const initiateSendEmailsAndSMS = (invitees, name, teeupUrl, type) => ({
  type: actionTypes.INITIATE_SEND_INVITES,
  payload: { invitees, name, teeupUrl, type },
})

const initiateEmitSocketInvitees = invited => ({
  type: actionTypes.INITIATE_EMIT_SOCKET_INVITES,
  payload: { invited },
})

const changeContactDefaultType = updatedContact => ({
  type: actionTypes.CONTACT_CHANGE_DEFAULT_TYPE,
  payload: { updatedContact },
})

const resyncContactsBook = () => ({
  type: actionTypes.RESYNC_CONTACTS_BOOK,
})

const getGoogleContacts = (accessToken, connectorEmail) => {
  customCalInstance({
    url: `${
      endpoints.googleContactsApi
    }${endpoints.contacts.getGoogleContacts()}`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  })
    .then(({ data }) => {
      const prepared = prepareGoogleContacts(data.connections, connectorEmail)
      store.dispatch(gotBookContacts(prepared))

      // A3 (Wave5-contacts, 2026-07-03): persist to People/Contacts so a Google sync survives a
      // reload, not just the current session -- prior to this the fetched contacts only ever
      // reached the in-memory ContactState.bookContacts module singleton (see
      // reducers/contactsReducer.js GOT_BOOK_CONTACTS), which every reload/new tab starts empty.
      // Same backend route the beta contract already defines for this
      // (api/routers/contacts.py POST /users/me/contacts/import). Best-effort: a failed persist
      // still leaves the just-synced contacts visible for the rest of THIS session via the
      // dispatch above, it just won't survive a reload -- never blocks/crashes the sync flow.
      if (prepared.length > 0) {
        api.client
          .post("/v1/users/me/contacts/import", {
            contacts: prepared.map(c => ({
              display_name: c.name,
              first_name: c.firstName,
              last_name: c.lastName,
              emails: (c.emails || []).map(e => e.email).filter(Boolean),
              phones: (c.phoneNumbers || []).map(p => p.number).filter(Boolean),
            })),
          })
          .catch(error => {
            console.log("contacts import (persist) error", error)
          })
      }
    })
    .catch(error => {
      throw error
    })
}

const getAppleContacts = (accessToken, connectorEmail) => {
  customCalInstance({
    url: `${endpoints.appleApi}${endpoints.contacts.getAppleContacts()}`,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  }).then(() => {
    // TODO: make a logic
  })
}

const linkSyncContacts = (accessToken, provider) => {
  return api.client
    .post(`${endpoints.contacts.linkContacts()}`, { signature: accessToken })
    .then(({ data: { connectors, isCreated } }) => {
      const newConnector = connectors.at(-1)

      if (!newConnector) return
      if (!isCreated) return { email: newConnector.email, isTryAnother: true }

      store.dispatch({
        type: actionTypes.SET_NEW_SYNC_CONTACT,
        payload: newConnector,
      })

      if (provider === loginProviders.google) {
        getGoogleContacts(newConnector.accessToken, newConnector.email)
      } else if (provider === loginProviders.apple) {
        getAppleContacts(newConnector.accessToken, newConnector.email)
      }

      return newConnector
    })
    .catch(error => {
      throw error
    })
}

const getContactsConnectors = () => {
  return api.client
    .get(`${endpoints.contacts.getContactsConnectors()}`)
    .then(({ data }) => {
      const connectors = Array.isArray(data?.connectors) ? data.connectors : []

      if (connectors.length === 0) return

      store.dispatch({
        type: actionTypes.SET_SYNC_CONTACTS_LIST,
        payload: connectors,
      })

      return connectors
    })
    .catch(() => {
      // Contacts-connectors lookup failing must never crash the settings page --
      // fail soft to "no connectors yet" (same UX as a genuinely empty list).
      return []
    })
}

// ACCOUNT-STATE bug B(ii) fix (2026-07-04): count-only summary of the signer's OWN
// public.contacts, so Sync Contacts can show the account's REAL existing synced state (e.g.
// contacts already synced from a phone over the years) independent of whether any web Google
// connector row exists. Never blocks/crashes the page -- a failure just means the row doesn't
// render, same as a genuinely empty account.
const getContactsSummary = () => {
  return api.client
    .get(`${endpoints.contacts.getContactsSummary()}`)
    .then(({ data }) => data)
    .catch(() => null)
}

// FIX 1 (contacts-default, 2026-07-03): iPhone-style default -- if the signed-in user has NO
// contacts connector yet but DOES have a Google sign-in identity, connect it automatically
// (no popup) instead of showing an empty "connect account to sync contacts" prompt. Reuses the
// same POST /users/contacts/connect the manual "Continue with Google" flow calls
// (linkSyncContacts above) -- the backend already resolves the signer's google-oauth2 identity
// server-side when no google-contacts-scoped signature is required
// (api/routers/contacts.py:_google_contacts_identity), so the CURRENT session's own access
// token (already attached to every api.client call) is enough as `signature`; there is no
// separate contacts-scoped token to mint for this default path. Failure (no Google identity,
// unresolvable token, etc.) must never surface as an error screen -- it just leaves today's
// connect prompt in place, so every failure mode here resolves to null instead of throwing.
const connectDefaultGoogleContacts = () => {
  const userInfo = getItem("userInfo")
  const signature = userInfo?.accessToken

  if (!signature) return Promise.resolve(null)

  return api.client
    .post(`${endpoints.contacts.linkContacts()}`, { signature })
    .then(({ data: { connectors, isCreated } }) => {
      const newConnector = connectors && connectors.at(-1)

      if (!newConnector) return null

      store.dispatch({
        type: actionTypes.SET_SYNC_CONTACTS_LIST,
        payload: connectors,
      })

      if (isCreated) {
        getGoogleContacts(newConnector.accessToken, newConnector.email)
      }

      return newConnector
    })
    .catch(error => {
      // Best-effort default: never block/crash the settings page over it -- fall back to
      // today's connect prompt exactly as if no default identity had been found.
      console.log("connectDefaultGoogleContacts error", error)
      return null
    })
}

// T-W2-F2 (2026-07-04): LinkedIn Connections-export upload + match-proposals queue
// (deterministic-interaction-ingestion-plan-2026-07-04.md S6a2). Confirmed live contract
// against api/routers/counterpart_search.py on coowe-api-dev before building this (never
// guessed): POST /v1/contacts/linkedin-import takes {filename, csv_text} (plain JSON, not
// multipart -- python-multipart isn't in the image), returns {batch_id, row_count, matched,
// proposals}. Proposals are GET /v1/contacts/linkedin-proposals?status=pending -> array of
// {id, row_ref, first_name, last_name, company, position, connected_on,
// candidate_contact_id, candidate_name, status}; resolve one via POST
// /v1/contacts/linkedin-proposals/{id}/confirm (body {contact_id?}, defaults to the
// candidate guess server-side) or .../reject. Email-matched rows attach attributes
// immediately server-side; unmatched rows NEVER auto-merge (Matt-Lock law) -- they land here
// as a proposal for the user to confirm or reject.
const uploadLinkedInExport = (filename, csvText) => {
  return api.client
    .post("/v1/contacts/linkedin-import", { filename, csv_text: csvText })
    .then(({ data }) => data)
}

const getLinkedInProposals = (status = "pending") => {
  return api.client
    .get("/v1/contacts/linkedin-proposals", { params: { status } })
    .then(({ data }) => (Array.isArray(data) ? data : []))
    .catch(() => [])
}

const confirmLinkedInProposal = (proposalId, contactId) => {
  return api.client
    .post(`/v1/contacts/linkedin-proposals/${proposalId}/confirm`, {
      contact_id: contactId,
    })
    .then(({ data }) => data)
}

const rejectLinkedInProposal = proposalId => {
  return api.client
    .post(`/v1/contacts/linkedin-proposals/${proposalId}/reject`)
    .then(({ data }) => data)
}

const unlinkContacts = (connectorId, connectorEmail) => {
  return api.client
    .delete(`${endpoints.contacts.unlinkContacts(connectorId)}`)
    .then(() => {
      store.dispatch({
        type: actionTypes.DELETE_CONTACTS_CONNECTOR,
        payload: connectorId,
      })

      store.dispatch({
        type: actionTypes.DELETE_SYNC_CONTACTS,
        payload: connectorEmail,
      })
    })
    .catch(error => {
      throw error
    })
}

export {
  getContacts,
  addTagsToUsers,
  removeTagsFromUsers,
  initiateSendEmailsAndSMS,
  initiateEmitSocketInvitees,
  resyncContactsBook,
  changeContactDefaultType,
  fetchBookContacts,
  getGoogleContacts,
  getAppleContacts,
  linkSyncContacts,
  getContactsSummary,
  getContactsConnectors,
  connectDefaultGoogleContacts,
  unlinkContacts,
  uploadLinkedInExport,
  getLinkedInProposals,
  confirmLinkedInProposal,
  rejectLinkedInProposal,
}
